import { useMemo } from "react";
import en from "@/i18n/hot-wallets-locales/en.json";
import ja from "@/i18n/hot-wallets-locales/ja.json";
import ko from "@/i18n/hot-wallets-locales/ko.json";
import ar from "@/i18n/hot-wallets-locales/ar.json";
import ptBr from "@/i18n/hot-wallets-locales/pt-br.json";
import es from "@/i18n/hot-wallets-locales/es.json";
import de from "@/i18n/hot-wallets-locales/de.json";
import fr from "@/i18n/hot-wallets-locales/fr.json";
import zhHk from "@/i18n/hot-wallets-locales/zh-hk.json";
import hi from "@/i18n/hot-wallets-locales/hi.json";

export interface HotWalletsLocaleCatalog {
  locale: string;
  dir: "ltr" | "rtl";
  ui: Record<string, string>;
}

/**
 * Locale catalog for the hot-wallets page shells (ALL-5039 / SPEC-HW-8 §3
 * rule 7: "all strings go through the shared catalogues, and every
 * supported language ships in the same PR"). Mirrors the shape and loading
 * pattern of src/i18n/use-error-locale.ts (ALL-4857, D636) — the one other
 * general-purpose, dependency-free locale catalog already in this app —
 * rather than introducing a second convention.
 */
export const HOT_WALLETS_LOCALES: Record<string, HotWalletsLocaleCatalog> = {
  en: en as HotWalletsLocaleCatalog,
  ja: ja as HotWalletsLocaleCatalog,
  ko: ko as HotWalletsLocaleCatalog,
  ar: ar as HotWalletsLocaleCatalog,
  "pt-br": ptBr as HotWalletsLocaleCatalog,
  es: es as HotWalletsLocaleCatalog,
  de: de as HotWalletsLocaleCatalog,
  fr: fr as HotWalletsLocaleCatalog,
  "zh-hk": zhHk as HotWalletsLocaleCatalog,
  hi: hi as HotWalletsLocaleCatalog,
};

export const DEFAULT_HOT_WALLETS_LOCALE = "en";

/** True only for a locale this catalogue actually ships. A plain
 * `HOT_WALLETS_LOCALES[code]` truthiness check also matches every inherited
 * `Object.prototype` member, so `?locale=constructor` (or `__proto__`,
 * `toString`, `hasOwnProperty`) resolved to `"constructor"` and the hook then
 * returned `Object` where a catalogue with a `.ui` record is required —
 * `ui["nav.overview"]` threw on mount. An own-key check keeps an unshipped
 * value unknown. */
function isShippedLocale(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(HOT_WALLETS_LOCALES, code);
}

/** Resolves the active locale: an explicit `?locale=` query param (used by
 * Playwright to cover 2+ locales deterministically) takes priority over the
 * browser's language, which takes priority over English. */
export function resolveHotWalletsLocale(searchParams: URLSearchParams): string {
  const requested = searchParams.get("locale");
  if (requested && isShippedLocale(requested)) return requested;

  if (typeof navigator !== "undefined") {
    const nav = navigator.language?.toLowerCase() ?? "";
    if (isShippedLocale(nav)) return nav;
    const short = nav.split("-")[0];
    if (isShippedLocale(short)) return short;
  }
  return DEFAULT_HOT_WALLETS_LOCALE;
}

/** Reads `?locale=` from the current URL (or an empty query string outside
 * the browser) and returns the matching catalog, memoized on that query
 * string. */
export function useHotWalletsLocale(): HotWalletsLocaleCatalog {
  const search = typeof window !== "undefined" ? window.location.search : "";
  return useMemo(() => {
    const code = resolveHotWalletsLocale(new URLSearchParams(search));
    return HOT_WALLETS_LOCALES[code] ?? HOT_WALLETS_LOCALES[DEFAULT_HOT_WALLETS_LOCALE];
  }, [search]);
}

/** Fills `{name}`-style placeholders in a localized template. */
export function formatTemplate(template: string, values: Record<string, string>): string {
  let out = template;
  for (const [key, value] of Object.entries(values)) {
    out = out.split(`{${key}}`).join(value);
  }
  return out;
}
